/**
 * The WT-5a invoice page's two writes, each drawn INLINE under the band (no
 * modals): Send and Void. Every refusal is a sentence in the refusal style
 * (wtInvoiceRefusal), never a success. There is no Mark paid here — that door
 * is Finance's (WT-5b).
 *
 *   Send   the recipient is prefilled from GET /recipient (the SAME resolver
 *          the send uses); To and CC are editable. The resolver's own address
 *          is sent as no `to`, so the API records where it came from. A
 *          blocked tax fallback is said, and Send is not offered (the API
 *          would answer `tax_fallback_blocked`). When the mail provider
 *          refuses (502 `email_not_sent`) the invoice is unchanged and the
 *          page says so; if the API ever carries a review or portal link on
 *          that refusal it is shown so the office can pass it on by hand
 *          (fieldiq-api#1254 sends none today).
 *   Void   a required reason; the API then releases the tickets and the page
 *          says which (WtReleasedNotice).
 */
import React, { useState } from 'react';
import { Ban, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useWtInvoiceRecipient, useSendWtInvoice, useVoidWtInvoice } from '../../../hooks/useWtInvoices';
import { formatInstant } from '../../../lib/calendar-date';
import { queryLoadFailure } from '../../../lib/load-failure';
import { wtInvoiceRefusal, wtInvoiceReviewUrl, wtListWords } from '../../../lib/wt-invoices';
import type { WtInvoice } from '../../../types/wt-invoices';
import { CcField, parseCcInput } from '../../downhole/sendBits';
import { WT_INPUT, WT_PRIMARY, WT_SECONDARY, WtField, WtFormError, WtPanel } from '../WtBits';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function WtInvoiceSendPanel({ inv, tz, onDone }: { inv: WtInvoice; tz: string | null; onDone: () => void }) {
  const recipient = useWtInvoiceRecipient(inv.id);
  const send = useSendWtInvoice();
  const r = recipient.data?.data ?? null;
  const resolved = r?.to?.email ?? '';
  const [to, setTo] = useState<string | null>(null);
  const [cc, setCc] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [reviewUrl, setReviewUrl] = useState<string | null>(null);
  const toValue = to ?? resolved;
  const ccParsed = parseCcInput(cc);
  const failure = queryLoadFailure(recipient);

  const submit = () => {
    setError(null);
    setReviewUrl(null);
    const typed = toValue.trim();
    if (!typed) return setError('Enter an address to send this invoice to.');
    if (!EMAIL_RE.test(typed)) return setError(`"${typed}" is not a valid email address.`);
    if (cc.trim() && ccParsed.problem) return setError(ccParsed.problem);
    send.mutate(
      { id: inv.id, cc: cc.trim() ? ccParsed.cc : undefined, ...(resolved && typed.toLowerCase() === resolved.toLowerCase() ? {} : { to: typed }) },
      {
        onSuccess: (res) => {
          toast.success(`${inv.invoiceNumber} sent to ${res?.sentTo ?? typed}`);
          onDone();
        },
        onError: (e) => {
          setError(wtInvoiceRefusal(e, 'The invoice was not sent.'));
          setReviewUrl(wtInvoiceReviewUrl(e));
        },
      },
    );
  };

  return (
    <WtPanel eyebrow="Send" title={`Email ${inv.invoiceNumber} with its PDF`} testId="wt-invoice-send-panel">
      <div className="space-y-3 px-4 py-4">
        <div data-testid="wt-invoice-recipient" className="rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-4 py-3 text-sm text-fieldiq-text">
          {recipient.isLoading ? (
            'Finding who to send it to…'
          ) : failure ? (
            'Who to send it to could not be read. Enter the address below.'
          ) : r?.to ? (
            <>
              Goes to <span className="font-bold text-white">{r.to.name ? `${r.to.name} <${r.to.email}>` : r.to.email}</span>
              {r.to.sourceLabel ? <span className="text-fieldiq-muted"> — {r.to.sourceLabel}</span> : null}
            </>
          ) : (
            <span>{r?.message ?? 'The customer has no accounting or main contact email. Type an address to send to.'}</span>
          )}
          {r?.lastSent && (
            <p className="mt-1 text-xs text-fieldiq-muted">
              Last sent {r.lastSent.at ? formatInstant(r.lastSent.at, tz) ?? '—' : '—'} to {r.lastSent.to || '—'}
              {r.lastSent.cc && r.lastSent.cc.length ? `, copied to ${r.lastSent.cc.join(', ')}` : ''}
            </p>
          )}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <WtField label="To" hint="The address above, unless you type another for this send.">
            <input className={WT_INPUT} type="email" inputMode="email" maxLength={255} value={toValue} onChange={(e) => setTo(e.target.value)} data-testid="wt-invoice-send-to" />
          </WtField>
          <CcField value={cc} onChange={setCc} problem={cc.trim() ? ccParsed.problem : null} />
        </div>
        <WtFormError message={error} />
        {reviewUrl && (
          <p data-testid="wt-invoice-send-review-url" className="text-sm text-fieldiq-text">
            The customer's review link, to pass on by hand:{' '}
            <a className="break-all font-mono underline" href={reviewUrl} target="_blank" rel="noreferrer">{reviewUrl}</a>
          </p>
        )}
        <div className="flex flex-wrap justify-end gap-2">
          <button type="button" className={WT_SECONDARY} onClick={onDone} disabled={send.isPending}>Cancel</button>
          <button type="button" className={WT_PRIMARY} onClick={submit} disabled={send.isPending} data-testid="wt-invoice-send-confirm">
            <Send className="size-4" aria-hidden="true" />
            {send.isPending ? 'Sending…' : 'Send with PDF'}
          </button>
        </div>
      </div>
    </WtPanel>
  );
}

/**
 * VOID NEEDS A REASON. The button stays enabled so a blank press is SAID
 * (the API's `reason_required` sentence), and nothing is sent until a reason
 * is typed.
 */
export function WtInvoiceVoidPanel({ inv, onDone, onVoided }: { inv: WtInvoice; onDone: () => void; onVoided: (releasedIds: string[]) => void }) {
  const voidInvoice = useVoidWtInvoice();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const live = inv.tickets.filter((t) => !t.releasedAt).map((t) => t.ticketNumber);
  return (
    <div data-testid="wt-invoice-void-confirm" className="space-y-3 rounded-fi-control border border-fi-amber/50 bg-fieldiq-card px-4 py-3">
      <WtFormError message={error} />
      <p className="text-sm text-fieldiq-text">
        Void {inv.invoiceNumber}? A void invoice is kept for the record and cannot be sent, paid or reopened.
        {live.length ? ` ${wtListWords(live)} ${live.length === 1 ? 'goes' : 'go'} back to the tickets waiting to be invoiced.` : ''}
      </p>
      <WtField label="Reason *">
        <textarea className={`${WT_INPUT} h-24 py-2`} maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="wt-invoice-void-reason" />
      </WtField>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={WT_SECONDARY} onClick={onDone} disabled={voidInvoice.isPending}>Keep invoice</button>
        <button
          type="button"
          disabled={voidInvoice.isPending}
          data-testid="wt-invoice-void-submit"
          className="inline-flex h-9 items-center gap-1.5 rounded-fi-control border border-black/20 bg-fi-signal-red px-3 text-sm font-bold text-fi-signal-ink disabled:cursor-not-allowed disabled:opacity-60"
          onClick={() => {
            setError(null);
            if (!reason.trim()) return setError('A reason is required to void an invoice.');
            voidInvoice.mutate(
              { id: inv.id, reason: reason.trim() },
              {
                onSuccess: (res) => { toast.success(`${inv.invoiceNumber} voided`); onVoided(res.releasedTicketIds); onDone(); },
                onError: (e) => setError(wtInvoiceRefusal(e, 'The invoice was not voided.')),
              },
            );
          }}
        >
          <Ban className="size-4" aria-hidden="true" />
          {voidInvoice.isPending ? 'Voiding…' : 'Void invoice'}
        </button>
      </div>
    </div>
  );
}

/** Said after a void: which tickets went back to waiting to be invoiced. A notice, not a success: amber edge, role="status". */
export function WtReleasedNotice({ numbers, onDismiss }: { numbers: string[]; onDismiss: () => void }) {
  return (
    <div role="status" data-testid="wt-invoice-released" className="flex items-start gap-3 rounded-fi-control border border-fi-amber/60 bg-fi-signal-amber/10 px-4 py-3 text-sm text-fieldiq-text">
      <p className="flex-1">
        {numbers.length
          ? `Released ${wtListWords(numbers)}. ${numbers.length === 1 ? 'It is' : 'They are'} waiting to be invoiced again and can go on a new invoice.`
          : 'No field ticket was released: none was still on this invoice.'}
      </p>
      <button type="button" className="text-xs font-bold underline" onClick={onDismiss}>Dismiss</button>
    </div>
  );
}
